import {
  ArrowPathIcon,
  BookOpenIcon,
  BuildingStorefrontIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  EnvelopeIcon,
  FlagIcon,
  HomeIcon,
  IdentificationIcon,
  InboxArrowDownIcon,
  InboxIcon,
  InformationCircleIcon,
  KeyIcon,
  PaperAirplaneIcon,
  ShareIcon,
  SparklesIcon,
  StarIcon,
  TruckIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  RectangleGroupIcon,
  BuildingLibraryIcon,
  GiftIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowPathIcon as ArrowPathIconSolid,
  BookOpenIcon as BookOpenIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  CalendarIcon as CalendarIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  ClockIcon as ClockIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  FlagIcon as FlagIconSolid,
  HomeIcon as HomeIconSolid,
  IdentificationIcon as IdentificationIconSolid,
  InboxArrowDownIcon as InboxArrowDownIconSolid,
  InboxIcon as InboxIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  KeyIcon as KeyIconSolid,
  PaperAirplaneIcon as PaperAirplaneIconSolid,
  ShareIcon as ShareIconSolid,
  SparklesIcon as SparklesIconSolid,
  StarIcon as StarIconSolid,
  TruckIcon as TruckIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  UsersIcon as UsersIconSolid,
  QuestionMarkCircleIcon as QuestionMarkSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  DevicePhoneMobileIcon as DevicePhoneMobileIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  RectangleGroupIcon as RectangleGroupIconSolid,
  BuildingLibraryIcon as BuildingLibraryIconSolid,
  GiftIcon as GiftIconSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverIconSolid,
} from "@heroicons/react/24/solid";
import Cookies from "js-cookie";
import { CSSProperties } from "styled-components";

import { LEGACY_MENTORSHIP_ENABLED } from "src/shared/constants/featureFlags";
import { HARDWARE_FEEDBACK_FORM_URL } from "src/shared/constants/hardware";
import { MENTOR_CAFE_NAV_COPY } from "src/shared/constants/mentorCafe";
import { Route } from "src/shared/constants/route";
import { Permission } from "src/shared/contexts/PermissionsContext/types";
import { PointsIcon } from "src/static/icons";

export type TSidebarSection = {
  title?: string;
  content: TSidebarItem[];
};

export type TSidebarItem = {
  name: string;
  href: string;
  onClick?: () => void;
  icon: React.FC<{ style?: CSSProperties }>;
  activeIcon: React.FC<{ style?: CSSProperties }>;
  permissions?: Permission[];
  antiPermissions?: Permission[];
  requiresHackerPostRsvpTools?: boolean;
  requiresTravelSupportBot?: boolean;
  requiresHardwareOpen?: boolean;
  requiresJudgingOpen?: boolean;
};

export const LEGACY_HACKER_MENTORSHIP_ITEM: TSidebarItem = {
  name: "Mentorship",
  href: Route.HACKER_MENTORSHIP,
  icon: UserGroupIcon,
  activeIcon: UserGroupIconSolid,
  permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
  requiresHackerPostRsvpTools: true,
};

export const GENERAL_SECTION: TSidebarSection = {
  content: [
    {
      name: "Home",
      href: Route.HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      antiPermissions: [
        Permission.SPONSOR,
        Permission.VOLUNTEER,
        Permission.WORKSHOP_LEAD,
      ],
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: "Welcome Booklet",
      href: Route.HACKER_WELCOME_BOOKLET,
      icon: BookOpenIcon,
      activeIcon: BookOpenIconSolid,
      permissions: [Permission.ATTENDEE_EVENT_ACCESS],
    },
    {
      name: "RSVP",
      href: Route.HACKER_RSVP,
      icon: ClipboardDocumentCheckIcon,
      activeIcon: ClipboardDocumentCheckIconSolid,
      permissions: [Permission.RSVP],
    },
    {
      name: "Travel Support",
      href: Route.HACKER_TRAVEL_SUPPORT,
      icon: PaperAirplaneIcon,
      activeIcon: PaperAirplaneIconSolid,
      requiresTravelSupportBot: true,
    },
    {
      name: "Schedule",
      href: Route.SCHEDULE,
      icon: CalendarIcon,
      activeIcon: CalendarIconSolid,
      permissions: [Permission.VIEW_FULL_SCHEDULE],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: MENTOR_CAFE_NAV_COPY.text,
      href: Route.HACKER_MENTOR_CAFE,
      icon: BuildingStorefrontIcon,
      activeIcon: BuildingStorefrontIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
    ...(LEGACY_MENTORSHIP_ENABLED ? [LEGACY_HACKER_MENTORSHIP_ITEM] : []),
  ],
};

export const HACKER_BADGE_SECTION: TSidebarSection = {
  title: "Hacker Badge",
  content: [
    {
      name: "Badge Info",
      href: Route.BADGE_MY_BADGE,
      icon: IdentificationIcon,
      activeIcon: IdentificationIconSolid,
      permissions: [Permission.ATTENDEE_EVENT_ACCESS],
    },
    {
      name: "Connect",
      href: Route.BADGE_CONNECT,
      icon: ShareIcon,
      activeIcon: ShareIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.MENTOR_EVENT_ACCESS,
        Permission.SPONSOR_EVENT_ACCESS,
        Permission.WORKSHOP_LEAD_EVENT_ACCESS,
      ],
    },
    {
      name: "Sync",
      href: Route.BADGE_SYNC,
      icon: ArrowPathIcon,
      activeIcon: ArrowPathIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.MENTOR_EVENT_ACCESS,
        Permission.SPONSOR_EVENT_ACCESS,
        Permission.WORKSHOP_LEAD_EVENT_ACCESS,
      ],
    },
    {
      name: "Admin Sync",
      href: Route.BADGE_ADMIN_SYNC,
      icon: WrenchScrewdriverIcon,
      activeIcon: WrenchScrewdriverIconSolid,
      permissions: [Permission.ORGANIZER],
    },
  ],
};

export const MENTOR_SECTIONS: TSidebarSection = {
  title: "Mentorship",
  content: [
    {
      name: "Mentor Home",
      href: Route.MENTOR_HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      permissions: [Permission.MENTOR],
    },
    {
      name: "Open Requests",
      href: Route.MENTOR_REQUESTS,
      icon: ClipboardDocumentListIcon,
      activeIcon: ClipboardDocumentListIconSolid,
      permissions: [Permission.MENTOR],
    },
    {
      name: "Claimed Requests",
      href: Route.MENTOR_CLAIMED_REQUESTS,
      icon: ClipboardDocumentCheckIcon,
      activeIcon: ClipboardDocumentCheckIconSolid,
      permissions: [Permission.MENTOR],
    },
    {
      name: "Shifts",
      href: Route.MENTOR_SHIFTS,
      icon: ClipboardDocumentListIcon,
      activeIcon: ClipboardDocumentListIconSolid,
      permissions: [Permission.ORGANIZER],
    },
    {
      name: "Shift Schedule",
      href: Route.MENTOR_SHIFT_SCHEDULE,
      icon: CalendarIcon,
      activeIcon: CalendarIconSolid,
      permissions: [Permission.MENTOR],
    },
    {
      name: "My Profile",
      href: Route.MENTOR_PROFILE,
      icon: UserCircleIcon,
      activeIcon: UserCircleIconSolid,
      permissions: [Permission.MENTOR],
    },
  ],
};

export const VOLUNTEER_SECTIONS: TSidebarSection = {
  title: "Volunteer",
  content: [
    {
      name: "Volunteer Home",
      href: Route.VOLUNTEER_HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      permissions: [Permission.VOLUNTEER_EVENT_ACCESS],
    },
    {
      name: "Contact",
      href: Route.VOLUNTEER_CONTACT,
      icon: EnvelopeIcon,
      activeIcon: EnvelopeIconSolid,
      permissions: [Permission.VOLUNTEER_EVENT_ACCESS],
    },
    {
      name: "Shift Schedule",
      href: Route.VOLUNTEER_SHIFT_SCHEDULE,
      icon: CalendarIcon,
      activeIcon: CalendarIconSolid,
      permissions: [Permission.VOLUNTEER_EVENT_ACCESS],
    },
    {
      name: "Shift Sign Up",
      href: Route.VOLUNTEER_SHIFTS,
      icon: ClipboardDocumentListIcon,
      activeIcon: ClipboardDocumentListIconSolid,
      permissions: [Permission.ORGANIZER],
    },
    {
      name: "Volunteer Scan",
      href: Route.VOLUNTEER_CHECK_IN,
      icon: DevicePhoneMobileIcon,
      activeIcon: DevicePhoneMobileIconSolid,
      permissions: [Permission.ORGANIZER],
    },
    {
      name: "Volunteer Admin",
      href: Route.VOLUNTEER_VOLUNTEERS,
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid,
      permissions: [Permission.ORGANIZER],
    },
    {
      name: "Registration",
      href: Route.VOLUNTEER_REGISTRATION,
      icon: InboxArrowDownIcon,
      activeIcon: InboxArrowDownIconSolid,
      permissions: [Permission.VOLUNTEER_EVENT_ACCESS],
    },
    {
      name: "Activity Scanning",
      href: Route.VOLUNTEER_EVENT_REDEMPTION,
      icon: CheckCircleIcon,
      activeIcon: CheckCircleIconSolid,
      permissions: [Permission.VOLUNTEER_EVENT_ACCESS],
    },
  ],
};

export const HARDWARE_SECTIONS: TSidebarSection = {
  title: "Hardware",
  content: [
    {
      name: "Hardware Home",
      href: Route.HARDWARE_HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.VOLUNTEER_EVENT_ACCESS,
      ],
      requiresHardwareOpen: true,
    },
    {
      name: "Inventory",
      href: Route.HARDWARE_INVENTORY,
      icon: InboxIcon,
      activeIcon: InboxIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.VOLUNTEER_EVENT_ACCESS,
      ],
      requiresHardwareOpen: true,
    },
    {
      name: "Request History",
      href: Route.HARDWARE_REQUEST_HISTORY,
      icon: ClockIcon,
      activeIcon: ClockIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.VOLUNTEER_EVENT_ACCESS,
      ],
      requiresHardwareOpen: true,
    },
    {
      name: "Users",
      href: Route.HARDWARE_USERS,
      icon: UsersIcon,
      activeIcon: UsersIconSolid,
      permissions: [Permission.ORGANIZER, Permission.VOLUNTEER_EVENT_ACCESS],
      requiresHardwareOpen: true,
    },
    {
      name: "Special Items",
      href: Route.HARDWARE_LOTTERY,
      icon: StarIcon,
      activeIcon: StarIconSolid,
      permissions: [Permission.ORGANIZER, Permission.VOLUNTEER_EVENT_ACCESS],
      requiresHardwareOpen: true,
    },
    {
      name: "FAQ",
      href: Route.HARDWARE_FAQ,
      icon: QuestionMarkCircleIcon,
      activeIcon: QuestionMarkSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.VOLUNTEER_EVENT_ACCESS,
      ],
      requiresHardwareOpen: true,
    },
    {
      name: "Feedback",
      href: HARDWARE_FEEDBACK_FORM_URL,
      icon: ChatBubbleLeftRightIcon,
      activeIcon: ChatBubbleLeftRightIconSolid,
      permissions: [
        Permission.HACKER_EVENT_ACCESS,
        Permission.VOLUNTEER_EVENT_ACCESS,
      ],
      requiresHardwareOpen: true,
    },
  ],
};

export const SPONSOR_SECTIONS: TSidebarSection = {
  title: "Sponsorship",
  content: [
    {
      name: "Home",
      href: Route.SPONSOR_HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      permissions: [Permission.SPONSOR],
    },
    {
      name: "Perks",
      href: Route.SPONSOR_PERKS,
      icon: SparklesIcon,
      activeIcon: SparklesIconSolid,
      permissions: [Permission.SPONSOR],
    },
    {
      name: "Shipped Swag",
      href: Route.SPONSOR_SHIPPED_SWAG,
      icon: TruckIcon,
      activeIcon: TruckIconSolid,
      permissions: [Permission.SPONSOR_SHIPPED_SWAG_ACCESS],
    },
    {
      name: "Recruitment",
      href: Route.SPONSOR_RECRUITMENT,
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid,
      permissions: [Permission.SPONSOR_RECRUITMENT_ACCESS],
    },
    {
      name: "Post-Event Report",
      href: Route.SPONSOR_POST_EVENT_REPORT,
      icon: DocumentTextIcon,
      activeIcon: DocumentTextIconSolid,
      permissions: [Permission.SPONSOR],
    },
  ],
};

export const INTERNAL_SECTIONS: TSidebarSection = {
  title: "Internal",
  content: [
    {
      name: "Impersonate",
      href: "#",
      onClick: () => {
        const token = window.prompt("JWT hackerapi access token");

        // The domain is:
        // local: `localhost`
        // staging: `.release-staging.my.hackthenorth.com`
        // deploy preview: `.deploy-preview-${number}--my-hackthenorth-com.netlify.app`
        // prod: `.hackthenorth.com`
        const domain =
          window.location.hostname === "my.hackthenorth.com"
            ? ".hackthenorth.com"
            : `.${window.location.hostname}`;

        if (token) {
          Cookies.set("accessJwt", token, {
            expires: Date.now() + 345600000,
            domain,
          });
        }
      },
      icon: KeyIcon,
      activeIcon: KeyIconSolid,
      permissions: [Permission.ORGANIZER],
    },
  ],
};

export const GOOSE_GAMES_SECTIONS: TSidebarSection = {
  title: "Goose Games",
  content: [
    {
      name: "The Nest",
      href: Route.GOOSE_GAMES_HOME,
      icon: HomeIcon,
      activeIcon: HomeIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: "Quests",
      href: Route.GOOSE_GAMES_QUESTS,
      icon: FlagIcon,
      activeIcon: FlagIconSolid,
      permissions: [Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: "Store",
      href: Route.GOOSE_GAMES_STORE,
      icon: PointsIcon,
      activeIcon: PointsIcon,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: "Hackers",
      href: Route.GOOSE_GAMES_HACKERS,
      icon: UsersIcon,
      activeIcon: UsersIconSolid,
      permissions: [Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
  ],
};

export const JUDGING_SECTIONS: TSidebarSection = {
  title: "Judging",
  content: [
    {
      name: "Judging Info",
      href: Route.HACKER_JUDGING_INFO,
      icon: InformationCircleIcon,
      activeIcon: InformationCircleIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresJudgingOpen: true,
    },
    {
      name: "Prizehub",
      href: Route.HACKER_JUDGING_PRIZEHUB,
      icon: GiftIcon,
      activeIcon: GiftIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresJudgingOpen: true,
    },
    {
      name: "Museum",
      href: Route.HACKER_JUDGING_MUSEUM,
      icon: BuildingLibraryIcon,
      activeIcon: BuildingLibraryIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresJudgingOpen: true,
    },
    {
      name: "FAQ",
      href: Route.HACKER_JUDGING_FAQ,
      icon: QuestionMarkCircleIcon,
      activeIcon: QuestionMarkSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresJudgingOpen: true,
    },
  ],
};

export const HACKER_DIRECTORY_SECTIONS: TSidebarSection = {
  title: "Hacker Directory",
  content: [
    {
      name: "How it works",
      href: Route.HACKER_DIRECTORY_HOME,
      icon: InformationCircleIcon,
      activeIcon: InformationCircleIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
    },
    {
      name: "Directory",
      href: Route.HACKER_DIRECTORY_DIRECTORY,
      icon: RectangleGroupIcon,
      activeIcon: RectangleGroupIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
      requiresHackerPostRsvpTools: true,
    },
    {
      name: "My Profile",
      href: Route.HACKER_DIRECTORY_YOUR_PROFILE,
      icon: UserCircleIcon,
      activeIcon: UserCircleIconSolid,
      permissions: [Permission.HACKER_EVENT_ACCESS, Permission.ORGANIZER],
    },
  ],
};

// const STAGING_SECTIONS = IS_PRODUCTION ? { content: [] } : HARDWARE_SECTIONS;

export const SECTIONS: TSidebarSection[] = [
  GENERAL_SECTION,
  HACKER_BADGE_SECTION,
  GOOSE_GAMES_SECTIONS,
  JUDGING_SECTIONS,
  HARDWARE_SECTIONS,
  HACKER_DIRECTORY_SECTIONS,
  ...(LEGACY_MENTORSHIP_ENABLED ? [MENTOR_SECTIONS] : []),
  VOLUNTEER_SECTIONS,
  SPONSOR_SECTIONS,
  INTERNAL_SECTIONS,
];
